/**
 * Web Push for this browser: find out whether it can, turn it on, turn it off.
 *
 * Works in Chrome, Edge and Firefox on a desktop or Android, installed or not, and in
 * Safari on iPhone and iPad (16.4+) only once the portal has been added to the home
 * screen — Apple does not deliver web push to a page in a Safari tab. `status()` says
 * which of those applies, so the settings can explain instead of failing silently.
 */

import { api } from '@/lib/api';

export type PushStatus =
  | 'unsupported'
  /** iPhone/iPad in a Safari tab: works after "Add to Home Screen". */
  | 'install-first'
  | 'denied'
  | 'off'
  | 'on';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  // The page script registers after load; in case it has not yet, do it now.
  return navigator.serviceWorker.register('/sw.js');
}

export async function status(): Promise<PushStatus> {
  if (isIos() && !standalone()) return 'install-first';
  if (!('PushManager' in window) || !('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** Ask, subscribe, and tell the server. Must run from a click: browsers require it. */
export async function turnOn(): Promise<PushStatus> {
  const { publicKey } = await api.webPushKey();
  if (!publicKey) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const reg = await registration();
  if (!reg) return 'unsupported';
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));

  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.subscribeWebPush({ endpoint: json.endpoint, keys: json.keys });
  return 'on';
}

export async function turnOff(): Promise<PushStatus> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.unsubscribeWebPush(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return 'off';
}

/**
 * On sign-out: this browser stops receiving this person's notifications. Best effort,
 * and before the session ends, while the API still accepts the token.
 */
export async function forgetThisBrowser(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.unsubscribeWebPush(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  } catch {
    // Nothing here may block a sign-out.
  }
}
